/**
 * Threat categories and their display config. The icon names map to Lucide
 * icons (see §12 of the build plan). Severity ordering is used everywhere a
 * "highest severity wins" rollup is needed.
 */

export const CATEGORIES = [
  'conflict',
  'seismic',
  'wildfire',
  'weather',
  'disaster',
  'health',
  'advisory',
] as const;

export type Category = (typeof CATEGORIES)[number];

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

/** Numeric rank so "max severity in a zone" is a simple comparison. */
export const SEVERITY_RANK: Record<Severity, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

export function maxSeverity(a: Severity, b: Severity): Severity {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

/** Map a normalized 0-100 score to a severity bucket. */
export function scoreToSeverity(score: number): Severity {
  if (score >= 80) return 'critical';
  if (score >= 60) return 'high';
  if (score >= 35) return 'medium';
  return 'low';
}

export const SOURCE_IDS = [
  'usgs',
  'gdelt',
  'gdacs',
  'reliefweb',
  'nws',
  'firms',
  'acled',
  'statedept',
] as const;

export type SourceId = (typeof SOURCE_IDS)[number];

export interface CategoryDisplay {
  label: string;
  /** Lucide icon name. */
  icon: string;
}

export const CATEGORY_DISPLAY: Record<Category, CategoryDisplay> = {
  conflict: { label: 'Conflict', icon: 'swords' },
  seismic: { label: 'Seismic', icon: 'activity' },
  wildfire: { label: 'Wildfire', icon: 'flame' },
  weather: { label: 'Weather', icon: 'cloud-rain' },
  disaster: { label: 'Disaster', icon: 'triangle-alert' },
  health: { label: 'Health', icon: 'biohazard' },
  advisory: { label: 'Advisory', icon: 'flag' },
};

/**
 * Deliberately desaturated palette (§12). Critical is the only true
 * saturated red, used sparingly.
 */
export const SEVERITY_COLOR: Record<Severity, string> = {
  low: '#3f8f5e',
  medium: '#c9a227',
  high: '#d97b29',
  critical: '#e23636',
};
